import { FormEvent } from "react";
import { DependencyLifeTime, Injectable } from "@miracledevs/paradigm-web-di";
import { ContextCollection } from "../contexts/DependencyContext";
import { FormElementType } from "../components/shared/Form/FormElementsFactory";
import moment from "moment";
import axios from "axios";
import { config } from "../../config";
import { trim } from "lodash";

interface RegExps {
    email: RegExp;
    string: RegExp;
    tel: RegExp;
    number: RegExp;
}

export const regExps: RegExps = {
    email: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    string: /^[A-Za-z.\s_-]+$/,
    tel: /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/,
    number: /^[0-9]*$/,
};

@Injectable({
    collection: ContextCollection,
    lifeTime: DependencyLifeTime.Singleton,
})
export class ValidationService {

    public sortValidationList(validations: Array<any>): Array<any>
    {
        const validationsValues = Object.values(validations);
        const validationsKeys: string[] = Object.keys(validations);
        if (validationsValues.every(v => v.order !== undefined)) {
            validationsValues.forEach((value, index) => value.key = validationsKeys[index]);
            validationsValues.sort((v1, v2) => v1.order - v2.order);

            const reorderedValidations: any = {};
            validationsValues.forEach(v => reorderedValidations[v.key] = validations[v.key]);
            return reorderedValidations;
        } else {
            return validations;
        }
    }

    public async setValidation(
        val: any,
        type: string,
        metadata?: any,
        name?: any,
        prevErrs?: string[]
    ): Promise<boolean> {
        let isValid = true;
        const metaValue =
            typeof metadata !== "object" ? metadata : metadata.val || metadata;

        switch (type) {
            case FormElementType.Text:
                if (val) isValid = this.validateInput(val, regExps.string);

                break;
            case FormElementType.Email:
                if (val) isValid = this.validateInput(val, regExps.email);

                break;
            case FormElementType.Tel:
                if (val) {
                    val = val.replace(/\D/g, "");
                    isValid = this.validateInput(val, regExps.tel);
                }

                break;
            case FormElementType.Number:
                isValid = this.validateInput(val, regExps.number);

                break;
            case "required":
                isValid = this.required(val);

                break;
            case "maxLength":
                isValid = this.maxLength(val, metaValue);

                break;
            case "minLength":
                isValid = this.minLength(val, metaValue);

                break;
            case "equalsTo":
                isValid = this.equalsTo(val, metaValue);

                break;
            case "date":
                if (val) isValid = this.isDate(val);
                break;
            case "minDate":
                if (val) isValid = this.validDate(val, metaValue, false);
                break;
            case "maxDate":
                if (val) isValid = this.validDate(val, metaValue, true);
                break;
            case "creditCard":
                if (val) isValid = this.isCreditCard(val);
                break;
            case "cardHolder":
                if (val)
                    isValid = this.isCardHolder(val);
                break;
            case "between":
                if (val) isValid = this.between(val, metaValue);
                break;
            case "remote":
                if (val) {
                    try {
                        if (name === "phone") {
                            val = val.replace(/[^A-Z0-9]/ig, "")
                        }

                        isValid = await this.remote(val, metaValue, name);
                    } catch (e) {
                        throw e;
                    }
                }
                break;
            case "validExpDate":
                if(val){
                    isValid = this.validExpDate(val, metaValue, false)
                }
                break;
            case "disabled":
                isValid = this.isDisabled(name);
                break;
            default:
                isValid = true;
        }

        return isValid;
    }

    private validateInput(val: any, regExp: RegExp): boolean {
        const validation = new RegExp(regExp);
        const ok = validation.test(val);

        return ok && val && val !== "";
    }

    private required(val: any): boolean {
        let isValid = false;

        if (typeof val === "string") {
            isValid = val && val.length > 0;
        } else {
            isValid = val;
        }

        return isValid;
    }

    private maxLength(val: any, length: number): boolean {
        return val ? val.length <= length : true;
    }

    private minLength(val: any, length: number): boolean {
        return val ? val.length >= length : true;
    }

    private equalsTo(password1: string, password2: string): boolean {
        return password1 === password2;
    }

    private isDate(date: Date): boolean {
        return moment(date).isValid();
    }

    private validDate(date: string, mDate: Date, isMax: boolean): boolean {
        let targetDate, currentDate;
        let _date = new Date(date);

        if (this.isDate(_date)) {
            currentDate = new Date(_date).getTime();
            targetDate = new Date(mDate).getTime();
            let isValid = isMax
                ? targetDate >= currentDate
                : targetDate <= currentDate;
            return isValid;
        }
    }

    private validExpDate(date: string, mDate: Date, isMax: boolean): boolean {
        let targetDate, currentDate;
        const [month, year] = date.split("/");
        const fullDate = `${month}/01/${year}`;

        currentDate = new Date(fullDate).getTime();
        targetDate = new Date(mDate).getTime();

        let isValid = isMax
            ? targetDate >= currentDate
            : targetDate <= currentDate;

        return isValid;
    }

    private between(date: Date, dateRange: Date[]): boolean {
        let isValid = true;
        const [min, max] = dateRange;
        if (
            date.getTime() <= new Date(min).getTime() &&
            date.getTime() > new Date(max).getTime()
        ) {
            isValid = false;
        }
        return isValid;
    }

    private isCreditCard(value: string) {
        /// accept only digits, dashes or spaces
        if (/[^0-9-\s]+/.test(value)) return false;

        // The Luhn Algorithm. It's so pretty.
        var nCheck = 0,
            nDigit = 0,
            bEven = false;
        value = value.replace(/\D/g, "");

        for (var n = value.length - 1; n >= 0; n--) {
            var cDigit = value.charAt(n),
                nDigit = parseInt(cDigit, 10);

            if (bEven) {
                if ((nDigit *= 2) > 9) nDigit -= 9;
            }

            nCheck += nDigit;
            bEven = !bEven;
        }

        return nCheck % 10 == 0;
    }

    private isCardHolder(value: string)
    {
        let parsed = value.trim();
        return parsed.length > 2;
    }

    private async remote(val: string, path: string, name?: string): Promise<boolean>
    {
        const response = await (axios.post(`${config.apiUrl}/${path}`, { [name]: val }, {
            headers: {
                "Authorization": "Bearer " + config.apiKey
            }
        }));

        return response.data.isValid;
    }

    private isDisabled(name: string)
    {
        if (!name) return true;

        const disabledElement = document.getElementsByName(name);
        return disabledElement && disabledElement.length > 0 && disabledElement[0].getAttribute("hasdisabledattr") !== "disabled";
    }
}
